import { v4 } from 'uuid';
import { defaultLayer, } from '@strength/db-schema';
/**
 * Serializes a fully merged Workout back into a single add-only WorkoutLayer.
 * The inverse of `mergeWorkoutLayers` for add-only layers: wrapping the result
 * in a HasWorkoutLayer (carrying the workout's name/assignedDate) and merging
 * reproduces the original workout. Non-'planned' statuses are not
 * representable in an add-only layer and are dropped.
 */
export function serializeWorkoutToLayer(workout) {
    return stripUndefined(serializeWorkout(workout));
}
function serializeWorkout(workout) {
    const sectionAdds = [];
    const supersetAdds = [];
    const supersetExerciseAdds = [];
    const alternateGroupAdds = [];
    const alternateExerciseAdds = [];
    const exerciseAdds = [];
    const exerciseMetricAdds = [];
    const setAdds = [];
    const setMetrics = [];
    for (const section of workout.sections) {
        sectionAdds.push({
            id: v4(),
            workoutSectionId: section.workoutSectionId,
            name: section.name,
            sortOrder: section.sortOrder,
            instruction: section.instruction,
            color: section.color,
        });
        serializeSectionEntries(section, {
            exerciseAdds,
            supersetAdds,
            supersetExerciseAdds,
            alternateGroupAdds,
            alternateExerciseAdds,
            exerciseMetricAdds,
            setAdds,
            setMetrics,
        });
    }
    return defaultLayer({
        sectionAdds,
        supersetAdds,
        supersetExerciseAdds,
        alternateGroupAdds,
        alternateExerciseAdds,
        exerciseAdds,
        exerciseMetricAdds,
        setAdds,
        setMetrics,
    });
}
function serializeSectionEntries(section, arrays) {
    for (const entry of section.entries) {
        switch (entry.__type) {
            case 'WorkoutExercise':
                serializeExercise(entry, entry.workoutSectionId, arrays);
                break;
            case 'WorkoutSuperset':
                serializeSuperset(entry, arrays);
                break;
            case 'WorkoutAlternateGroup':
                serializeAlternateGroup(entry, arrays);
                break;
        }
    }
}
function serializeSuperset(superset, arrays) {
    arrays.supersetAdds.push({
        id: v4(),
        workoutSectionId: superset.workoutSectionId,
        workoutSupersetId: superset.workoutSupersetId,
        sortOrder: superset.sortOrder,
    });
    for (const supersetExercise of superset.exercises) {
        arrays.supersetExerciseAdds.push({
            id: v4(),
            workoutSupersetId: superset.workoutSupersetId,
            workoutSupersetExerciseId: supersetExercise.workoutSupersetExerciseId,
            workoutExerciseId: supersetExercise.workoutExerciseId,
            sortOrder: supersetExercise.sortOrder,
        });
        serializeExercise(supersetExercise.exercise, superset.workoutSectionId, arrays);
    }
}
function serializeAlternateGroup(group, arrays) {
    arrays.alternateGroupAdds.push({
        id: v4(),
        workoutSectionId: group.workoutSectionId,
        workoutAlternateGroupId: group.workoutAlternateGroupId,
        sortOrder: group.sortOrder,
        selectedExerciseId: group.selectedExerciseId,
    });
    for (const alternateExercise of group.exercises) {
        arrays.alternateExerciseAdds.push({
            id: v4(),
            workoutAlternateGroupId: group.workoutAlternateGroupId,
            workoutAlternateExerciseId: alternateExercise.workoutAlternateExerciseId,
            workoutExerciseId: alternateExercise.workoutExerciseId,
            sortOrder: alternateExercise.sortOrder,
        });
        serializeExercise(alternateExercise.exercise, group.workoutSectionId, arrays);
    }
}
function serializeExercise(exercise, workoutSectionId, arrays) {
    arrays.exerciseAdds.push({
        id: v4(),
        workoutExerciseId: exercise.workoutExerciseId,
        workoutSectionId,
        libraryExerciseId: exercise.libraryExerciseId,
        exerciseName: exercise.exerciseName,
        instruction: exercise.instruction,
        sortOrder: exercise.sortOrder,
        isEachSide: exercise.isEachSide,
    });
    for (const metric of exercise.metrics) {
        arrays.exerciseMetricAdds.push({
            id: v4(),
            workoutExerciseMetricId: metric.workoutExerciseMetricId,
            sortOrder: metric.sortOrder,
            workoutExerciseId: metric.workoutExerciseId,
            metricId: metric.metricId,
            metricName: metric.metricName,
            metricType: metric.metricType,
            benchmarkGridId: metric.benchmarkGridId,
        });
    }
    for (const set of exercise.sets) {
        arrays.setAdds.push({
            id: v4(),
            workoutSetId: set.workoutSetId,
            workoutExerciseId: set.workoutExerciseId,
            sortOrder: set.sortOrder,
            workoutSetGroupId: set.workoutSetGroupId,
        });
        for (const metric of set.metrics) {
            arrays.setMetrics.push({
                id: v4(),
                workoutSetId: set.workoutSetId,
                metricId: metric.metricId,
                forCollection: metric.forCollection,
                input: metric.input,
                reps: metric.reps,
                dataSource: metric.dataSource,
            });
        }
    }
}
/** Removes keys whose value is undefined (mutates in place, returns input). */
function stripUndefined(value) {
    for (const key of Object.keys(value)) {
        const v = value[key];
        if (v === undefined) {
            delete value[key];
        }
        else if (typeof v === 'object' && v !== null) {
            stripUndefined(v);
        }
    }
    return value;
}
//# sourceMappingURL=serialize-workout-to-layer.js.map